import React, { useState, useEffect, useCallback, useContext } from 'react';
import { authFetch } from './authFetch';
import { UserContext } from './App';
import { BarChart3, Users, Clock, Activity, Filter, RefreshCw, ShieldCheck } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminAgentReport() {
  const { currentUser } = useContext(UserContext) || { currentUser: null };
  const isAdmin = currentUser?.is_staff || (currentUser?.groups || []).some(g => (g.name || '').toLowerCase() === 'admin');

  const [activeTab, setActiveTab] = useState('activity');
  const [activityData, setActivityData] = useState([]);
  const [conversionData, setConversionData] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [loadingConversion, setLoadingConversion] = useState(false);
  const [error, setError] = useState(null);

  // Conversion report filters
  const [filterAgentId, setFilterAgentId] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const fetchActivity = useCallback(async () => {
    setLoadingActivity(true);
    setError(null);
    try {
      const res = await authFetch(`${BASE_URL}/api/admin/agent-activity/`);
      if (!res.ok) throw new Error('Failed to fetch agent activity');
      const data = await res.json();
      setActivityData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingActivity(false);
    }
  }, []);

  const fetchConversion = useCallback(async () => {
    setLoadingConversion(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterAgentId) params.append('agent_id', filterAgentId);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);

      const res = await authFetch(`${BASE_URL}/api/admin/conversion-report/?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch conversion report');
      const data = await res.json();
      setConversionData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingConversion(false);
    }
  }, [filterAgentId, filterDateFrom, filterDateTo]);

  useEffect(() => {
    if (isAdmin) {
      fetchActivity();
      fetchConversion();
    }
  }, [isAdmin, fetchActivity, fetchConversion]);

  if (!isAdmin) {
    return (
      <div className="bg-red-50 border border-red-300 rounded-lg p-8 text-center mt-8">
        <ShieldCheck className="h-12 w-12 text-red-400 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-red-800">Access Denied</h2>
        <p className="text-red-600 mt-2">This page is only available to administrators.</p>
      </div>
    );
  }

  const formatDate = (iso) => {
    if (!iso) return 'Never';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  // Totals for conversion report
  const convTotals = conversionData.reduce((acc, r) => ({
    total: acc.total + r.total_aging_in,
    sold: acc.sold + r.sold,
    policies: acc.policies + r.active_policies,
  }), { total: 0, sold: 0, policies: 0 });
  const convTotalRate = convTotals.total > 0 ? ((convTotals.sold + convTotals.policies) / convTotals.total * 100).toFixed(1) : '0.0';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <BarChart3 className="h-7 w-7 text-blue-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Agent Report</h1>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm">{error}</div>
      )}

      {/* Tab bar */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-6">
          <button
            onClick={() => setActiveTab('activity')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'activity' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Activity className="h-4 w-4 inline mr-1" />
            Agent Activity
          </button>
          <button
            onClick={() => setActiveTab('conversion')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'conversion' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <BarChart3 className="h-4 w-4 inline mr-1" />
            Conversion Report
          </button>
        </nav>
      </div>

      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800">Agent Activity</h2>
            <button onClick={fetchActivity} disabled={loadingActivity} className="flex items-center text-sm text-blue-600 hover:text-blue-800">
              <RefreshCw className={`h-4 w-4 mr-1 ${loadingActivity ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {loadingActivity ? (
            <div className="p-8 text-center text-gray-500">Loading agent activity...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Logins (7d)</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Logins (30d)</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Customers</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {activityData.map(agent => (
                    <tr key={agent.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-gray-900">{agent.full_name}</td>
                      <td className="px-6 py-3 text-sm">
                        <span className={`inline-flex items-center ${agent.is_active_24h ? 'text-green-600' : 'text-gray-400'}`}>
                          <span className={`h-2.5 w-2.5 rounded-full mr-2 ${agent.is_active_24h ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          {agent.is_active_24h ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-600">{formatDate(agent.last_login)}</td>
                      <td className="px-6 py-3 text-sm text-gray-900 text-right">{agent.login_count_7d}</td>
                      <td className="px-6 py-3 text-sm text-gray-900 text-right">{agent.login_count_30d}</td>
                      <td className="px-6 py-3 text-sm text-gray-900 text-right">{agent.total_customers}</td>
                    </tr>
                  ))}
                  {activityData.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-gray-400">No agent data available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Conversion Tab */}
      {activeTab === 'conversion' && (
        <div>
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <div className="flex items-center space-x-4 flex-wrap">
              <div className="flex items-center">
                <Filter className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-700 mr-2">Filters:</span>
              </div>
              <div>
                <label className="text-xs text-gray-500 block">Agent</label>
                <select
                  value={filterAgentId}
                  onChange={e => setFilterAgentId(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="">All Agents</option>
                  {activityData.map(a => (
                    <option key={a.id} value={a.id}>{a.full_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block">From</label>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={e => setFilterDateFrom(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block">To</label>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={e => setFilterDateTo(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                />
              </div>
              <div className="pt-4">
                <button
                  onClick={fetchConversion}
                  disabled={loadingConversion}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  {loadingConversion ? 'Loading...' : 'Apply'}
                </button>
              </div>
            </div>
          </div>

          {/* Conversion Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">Aging-In Conversion Rates</h2>
            </div>

            {loadingConversion ? (
              <div className="p-8 text-center text-gray-500">Loading conversion data...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aging-In Leads</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sold</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Active Policies</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Conversion Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {conversionData.map(row => (
                      <tr key={row.agent_id} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-sm font-medium text-gray-900">{row.agent_name}</td>
                        <td className="px-6 py-3 text-sm text-gray-900 text-right">{row.total_aging_in}</td>
                        <td className="px-6 py-3 text-sm text-gray-900 text-right">{row.sold}</td>
                        <td className="px-6 py-3 text-sm text-gray-900 text-right">{row.active_policies}</td>
                        <td className="px-6 py-3 text-right">
                          <span className={`text-sm font-semibold ${
                            row.conversion_rate >= 50 ? 'text-green-600' :
                            row.conversion_rate >= 25 ? 'text-yellow-600' : 'text-gray-600'
                          }`}>
                            {row.conversion_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                    {conversionData.length > 1 && (
                      <tr className="bg-gray-50 font-semibold">
                        <td className="px-6 py-3 text-sm text-gray-900">Totals</td>
                        <td className="px-6 py-3 text-sm text-gray-900 text-right">{convTotals.total}</td>
                        <td className="px-6 py-3 text-sm text-gray-900 text-right">{convTotals.sold}</td>
                        <td className="px-6 py-3 text-sm text-gray-900 text-right">{convTotals.policies}</td>
                        <td className="px-6 py-3 text-sm text-gray-900 text-right">{convTotalRate}%</td>
                      </tr>
                    )}
                    {conversionData.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-400">No conversion data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAgentReport;
